// Align & distribute (DRAW_PLAN §1 "Arrange"): align edges / centres to the selection, a key object (click an
// object of the selection again to make it the key) or the artboard; distribute edges / centres or equal
// spacing (automatic, or an exact gap in document units). One undo step per click.
import {
  AlignCenterHorizontal,
  AlignCenterVertical,
  AlignEndHorizontal,
  AlignEndVertical,
  AlignHorizontalDistributeCenter,
  AlignHorizontalDistributeEnd,
  AlignHorizontalDistributeStart,
  AlignHorizontalSpaceAround,
  AlignStartHorizontal,
  AlignStartVertical,
  AlignVerticalDistributeCenter,
  AlignVerticalDistributeEnd,
  AlignVerticalDistributeStart,
  AlignVerticalSpaceAround,
  type LucideIcon,
} from 'lucide-react';
import { useState } from 'react';
import { useT, type MessageKey } from '../../../i18n';
import { useDesign } from '../../state';
import { useDesignUi } from '../designUi';
import { currentList } from '../../render/instance';
import { selectionBounds } from '../../tools/selection';
import { align, distribute, union, type AlignOp, type DistributeOp } from '../../geometry/align';
import { transformNodes } from '../../commands/commands';
import { findNode } from '../../model/tree';
import { toPt } from '../../model/units';
import type { Mat2D, Rect } from '../../model/types';
import { TransformPanel } from './TransformPanel';

type Ref = 'selection' | 'key' | 'artboard';
const ALIGN: { op: AlignOp; label: MessageKey; icon: LucideIcon }[] = [
  { op: 'left', label: 'alLeft', icon: AlignStartVertical },
  { op: 'hcenter', label: 'alHCenter', icon: AlignCenterVertical },
  { op: 'right', label: 'alRight', icon: AlignEndVertical },
  { op: 'top', label: 'alTop', icon: AlignStartHorizontal },
  { op: 'vcenter', label: 'alVCenter', icon: AlignCenterHorizontal },
  { op: 'bottom', label: 'alBottom', icon: AlignEndHorizontal },
];
const DIST: { op: DistributeOp; label: MessageKey; icon: LucideIcon }[] = [
  { op: 'left', label: 'diLeft', icon: AlignHorizontalDistributeStart },
  { op: 'hcenter', label: 'diHCenter', icon: AlignHorizontalDistributeCenter },
  { op: 'right', label: 'diRight', icon: AlignHorizontalDistributeEnd },
  { op: 'top', label: 'diTop', icon: AlignVerticalDistributeStart },
  { op: 'vcenter', label: 'diVCenter', icon: AlignVerticalDistributeCenter },
  { op: 'bottom', label: 'diBottom', icon: AlignVerticalDistributeEnd },
  { op: 'hspace', label: 'diHSpace', icon: AlignHorizontalSpaceAround },
  { op: 'vspace', label: 'diVSpace', icon: AlignVerticalSpaceAround },
];

function boxes(): Map<string, Rect> {
  const list = currentList();
  const m = new Map<string, Rect>();
  for (const id of useDesignUi.getState().selection) {
    const b = selectionBounds(list, [id]);
    if (b) m.set(id, b);
  }
  return m;
}

function applyMoves(moves: Map<string, [number, number]>) {
  const st = useDesign.getState();
  if (!st.doc) return;
  const next = new Map<string, Mat2D>();
  for (const [id, [dx, dy]] of moves) {
    if (!dx && !dy) continue;
    const f = findNode(st.doc, id);
    if (!f) continue;
    const t = f.node.transform;
    next.set(id, [t[0], t[1], t[2], t[3], t[4] + dx, t[5] + dy]);
  }
  if (next.size) st.run(transformNodes(next));
}

export function AlignPanel() {
  const t = useT();
  const doc = useDesign((st) => st.doc)!;
  const sel = useDesignUi((st) => st.selection);
  const key = useDesignUi((st) => st.keyObject);
  const [ref, setRef] = useState<Ref>('selection');
  const [gap, setGap] = useState('');
  const refBox = (bs: Map<string, Rect>): Rect | null => {
    if (ref === 'artboard') {
      const all = union([...bs.values()]);
      const cx = all.x + all.w / 2,
        cy = all.y + all.h / 2;
      return (
        doc.artboards.find((a) => cx >= a.x && cx <= a.x + a.w && cy >= a.y && cy <= a.y + a.h) ??
        doc.artboards[0] ??
        null
      );
    }
    if (ref === 'key') return key && bs.get(key) ? bs.get(key)! : null;
    return union([...bs.values()]);
  };
  const doAlign = (op: AlignOp) => {
    const bs = boxes();
    if (!bs.size) return;
    const r = refBox(bs);
    if (!r) return;
    const moves = align(bs, op, r);
    if (ref === 'key' && key) moves.delete(key);
    applyMoves(moves);
  };
  const doDistribute = (op: DistributeOp) => {
    const bs = boxes();
    if (bs.size < 2) return;
    const fixed = gap.trim() === '' ? undefined : toPt(Number(gap), doc.units, doc.rasterDpi);
    applyMoves(
      distribute(
        bs,
        op,
        Number.isFinite(fixed) ? fixed : undefined,
        ref === 'key' ? (key ?? undefined) : undefined,
      ),
    );
  };
  const disabled = !sel.length;
  return (
    <div data-testid="align-panel" style={{ display: 'grid', gap: 6 }}>
      <label>
        {t('alignTo')}{' '}
        <select data-testid="align-to" value={ref} onChange={(e) => setRef(e.target.value as Ref)}>
          <option value="selection">{t('alignToSelection')}</option>
          <option value="key">{t('alignToKey')}</option>
          <option value="artboard">{t('alignToArtboard')}</option>
        </select>
      </label>
      {ref === 'key' && !key && <span style={{ opacity: 0.7 }}>{t('keyHint')}</span>}
      <div style={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {ALIGN.map((a) => (
          <button
            key={a.op}
            data-testid={`align-${a.op}`}
            disabled={disabled}
            title={t(a.label)}
            aria-label={t(a.label)}
            onClick={() => doAlign(a.op)}
            style={{ width: 30 }}
          >
            <a.icon className="icon" aria-hidden="true" />
          </button>
        ))}
      </div>
      <strong>{t('distribute')}</strong>
      <div style={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {DIST.map((a) => (
          <button
            key={a.op}
            data-testid={`dist-${a.op}`}
            disabled={sel.length < 2}
            title={t(a.label)}
            aria-label={t(a.label)}
            onClick={() => doDistribute(a.op)}
            style={{ width: 30 }}
          >
            <a.icon className="icon" aria-hidden="true" />
          </button>
        ))}
      </div>
      <label>
        {t('spacing')}{' '}
        <input
          data-testid="dist-gap"
          value={gap}
          placeholder={t('auto')}
          onChange={(e) => setGap(e.target.value)}
          style={{ width: 70 }}
        />{' '}
        {doc.units}
      </label>
    </div>
  );
}

export function ArrangeDock() {
  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <AlignPanel />
      <TransformPanel />
    </div>
  );
}
